import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Filter,
  Layers3,
  Loader2,
  Play,
  Search,
} from 'lucide-react'
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
} from 'ag-grid-community'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { useDebounce } from '@/hooks/useDebounce'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { useWaveRollupSelectionStore } from '@/stores/useWaveRollupSelectionStore'
import {
  calcPathLabel,
  wavePlanMfpLabel,
  wavePlanPath,
  wavePlanStatusLabel,
  MAX_ROLLUP_PLANS,
  waveRollupPath,
} from '@/components/WavePlanning/routes'
import { WavePlanStatus } from '@/components/WavePlanning/WavePlanStatus'
import '@/components/WavePlanning/wave-planning.css'
import { WavePlanLoadError } from '@/components/WavePlanning/WavePlanPageShell'
import {
  focusCellControl,
  keepNativeKeys,
} from '@/components/WavePlanning/gridEditing'
import type { WavePlanListItem, WavePlanStatusValue } from '@/api/wavePlans'
import { useCreateWavePlan, useWavePlanList } from '@/hooks/useWavePlans'
import { getStatusLabel } from '@/utils/statusConfig'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type { ApiPlanStatus } from '@/types'

ModuleRegistry.registerModules([AllCommunityModule])

const defaultColDef: ColDef<WavePlanListItem> = {
  suppressKeyboardEvent: ({ event }) => keepNativeKeys(event),
}

const STATUSES: WavePlanStatusValue[] = [
  'NOT_STARTED',
  'DRAFT',
  'IN_PROGRESS',
  'SUBMITTED',
  'APPROVED',
  'SENT_BACK',
]

function StartWavePlanButton({
  data,
}: CustomCellRendererProps<WavePlanListItem>) {
  const navigate = useNavigate()
  const create = useCreateWavePlan()
  if (!data || data.id) return null
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={create.isPending}
      onClick={event => {
        event.stopPropagation()
        create.mutate(data.assortment_plan_id, {
          onSuccess: plan => navigate(wavePlanPath(plan.id)),
        })
      }}
    >
      {create.isPending ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Play size={14} />
      )}
      Start wave plan
    </Button>
  )
}

const columnDefs: ColDef<WavePlanListItem>[] = [
  {
    colId: 'name',
    headerName: 'Plan Name',
    initialFlex: 2,
    minWidth: 260,
    cellRenderer: ({ data }: CustomCellRendererProps<WavePlanListItem>) =>
      data && (
        <div className="flex min-w-0 flex-col justify-center gap-1 leading-normal">
          {data.id ? (
            <Link
              to={wavePlanPath(data.id)}
              title={data.assortment_plan_name}
              onClick={event => event.stopPropagation()}
              className="truncate text-sm font-medium text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              {data.assortment_plan_name}
            </Link>
          ) : (
            <span
              className="truncate text-sm font-medium"
              title={data.assortment_plan_name}
            >
              {data.assortment_plan_name}
            </span>
          )}
          <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            {[
              data.season_name,
              data.fiscal_year && `FY${data.fiscal_year}`,
              data.calc_path && calcPathLabel(data.calc_path),
            ]
              .filter(Boolean)
              .join(' · ')}
            {data.is_stale && (
              <span className="inline-flex items-center gap-0.5 text-amber-800">
                <AlertTriangle size={11} /> AP changed
              </span>
            )}
          </span>
        </div>
      ),
  },
  {
    colId: 'status',
    headerName: 'Wave plan',
    minWidth: 150,
    initialFlex: 1,
    cellRenderer: ({ data }: CustomCellRendererProps<WavePlanListItem>) =>
      data && <WavePlanStatus status={data.status} />,
  },
  {
    colId: 'apStatus',
    headerName: 'Assortment plan',
    minWidth: 150,
    initialFlex: 1,
    valueGetter: ({ data }) =>
      data ? getStatusLabel(data.assortment_plan_status as ApiPlanStatus) : '',
    cellClass: 'text-sm',
  },
  {
    colId: 'mfps',
    headerName: 'MFP Plan',
    minWidth: 200,
    initialFlex: 2,
    valueGetter: ({ data }) => (data ? wavePlanMfpLabel(data) : ''),
    tooltipValueGetter: ({ value }) => value,
    wrapText: true,
    autoHeight: true,
    cellClass: 'text-sm',
  },
  {
    colId: 'skus',
    headerName: 'SKUs',
    initialWidth: 90,
    valueGetter: ({ data }) => data?.sku_count ?? '—',
    cellClass: 'text-sm tabular-nums',
  },
  {
    colId: 'action',
    headerName: '',
    initialWidth: 180,
    sortable: false,
    cellRenderer: StartWavePlanButton,
  },
]

function PlanSelectionCell({
  data,
}: CustomCellRendererProps<WavePlanListItem>) {
  const { selectedPlanIds, selectPlans } = useWaveRollupSelectionStore()
  if (!data) return null
  const id = data.id
  return (
    <div
      data-rollup-selection
      className="flex h-full items-center justify-center"
      title={id ? undefined : 'Start the wave plan to include it in a rollup'}
      onClick={event => event.stopPropagation()}
    >
      <Checkbox
        aria-label={`Select ${data.assortment_plan_name}`}
        disabled={!id}
        checked={!!id && selectedPlanIds.includes(id)}
        onCheckedChange={checked => id && selectPlans([id], checked)}
        className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:border-muted-foreground/40 peer-disabled:bg-muted"
      />
    </div>
  )
}

function PlanSelectionHeader({ planIds }: { planIds: string[] }) {
  const { selectedPlanIds, selectPlans } = useWaveRollupSelectionStore()
  const count = planIds.filter(id => selectedPlanIds.includes(id)).length
  return (
    <Checkbox
      aria-label="Select all started plans on this page"
      title="Select all started plans on this page"
      checked={planIds.length > 0 && count === planIds.length}
      indeterminate={count > 0 && count < planIds.length}
      disabled={!planIds.length}
      onCheckedChange={checked => selectPlans(planIds, checked)}
      className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
    />
  )
}

export default function WavePlans() {
  const navigate = useNavigate()
  const { selectedPlanIds, setSelectedPlanIds } = useWaveRollupSelectionStore()
  const tooManySelected = selectedPlanIds.length > MAX_ROLLUP_PLANS
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search.trim(), 300)
  const [status, setStatus] = useState<'all' | WavePlanStatusValue>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.WAVE_PLANS_LIST,
    20
  )
  const list = useWavePlanList({
    offset: (page - 1) * pageSize,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
  })
  const rows = useMemo(() => list.data?.data ?? [], [list.data])
  const total = list.data?.pagination.total ?? 0
  const startedIds = useMemo(
    () => rows.flatMap(row => (row.id ? [row.id] : [])),
    [rows]
  )
  const selectableColumns = useMemo<ColDef<WavePlanListItem>[]>(
    () => [
      {
        colId: 'rollupSelection',
        headerName: '',
        width: 52,
        pinned: 'left',
        sortable: false,
        resizable: false,
        headerComponent: PlanSelectionHeader,
        headerComponentParams: { planIds: startedIds },
        cellRenderer: PlanSelectionCell,
      },
      ...columnDefs,
    ],
    [startedIds]
  )
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="flex min-w-0 flex-col gap-6 p-4 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">
          Wave Planning
        </h1>
        <p className="mt-1 text-base text-foreground">
          Open a plan to manage allocations, or select plans to view a rollup.
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <div className="relative w-full sm:max-w-[352px] sm:flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search plans"
            placeholder="Search plans..."
            value={search}
            onChange={event => {
              setSearch(event.target.value)
              setPage(1)
            }}
            className="bg-muted/50 pl-9"
          />
        </div>
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <Filter className="h-5 w-5 shrink-0 text-muted-foreground" />
          <Select
            value={status}
            onValueChange={value => {
              setStatus(value as typeof status)
              setPage(1)
            }}
          >
            <SelectTrigger
              aria-label="Filter by wave plan status"
              className="min-w-0 flex-1 sm:w-[180px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(value => (
                <SelectItem key={value} value={value}>
                  {wavePlanStatusLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground sm:ml-auto" role="status">
          {total} {total === 1 ? 'plan' : 'plans'}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <p
          className="text-sm font-medium"
          role="status"
          data-testid="rollup-selection-count"
        >
          {selectedPlanIds.length} selected
          {tooManySelected && (
            <span className="ml-2 font-normal text-destructive">
              (rollup supports up to {MAX_ROLLUP_PLANS} plans)
            </span>
          )}
        </p>
        <Button
          variant="ghost"
          size="sm"
          disabled={!selectedPlanIds.length}
          onClick={() => setSelectedPlanIds([])}
        >
          Clear selection
        </Button>
        <Button
          className="sm:ml-auto"
          disabled={!selectedPlanIds.length || tooManySelected}
          onClick={() => navigate(waveRollupPath(selectedPlanIds))}
        >
          <Layers3 size={16} /> View Rollup
        </Button>
      </div>
      {list.isError ? (
        <WavePlanLoadError
          className="m-0"
          error={list.error}
          onRetry={() => void list.refetch()}
          unavailable={{
            title: 'The plans could not be loaded',
            message: getErrorMessage(
              list.error,
              'Check the filters and try again.'
            ),
          }}
          failedTitle="The plans could not be loaded"
        />
      ) : (
        <div className="min-w-0 overflow-hidden border border-[#e7e7e7] bg-white">
          <div
            className="wave-plans-grid ag-theme-michaels w-full"
            data-testid="wave-plans-grid"
          >
            <AgGridReact<WavePlanListItem>
              rowData={rows}
              columnDefs={selectableColumns}
              defaultColDef={defaultColDef}
              getRowId={({ data }) => data.assortment_plan_id}
              theme="legacy"
              domLayout="autoHeight"
              rowHeight={68}
              headerHeight={48}
              loading={list.isLoading}
              overlayNoRowsTemplate="<span>No plans found</span>"
              onCellKeyDown={focusCellControl}
              suppressMovableColumns
              suppressColumnVirtualisation
              rowClass="cursor-pointer"
              onRowClicked={({ data, event }) => {
                if (
                  event?.target instanceof Element &&
                  event.target.closest('[data-rollup-selection]')
                )
                  return
                if (data?.id) navigate(wavePlanPath(data.id))
              }}
            />
          </div>
          <div className="[&>div]:flex-wrap [&>div]:gap-3">
            <Pagination
              page={Math.min(page, totalPages)}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={value => {
                setPageSize(value)
                setPage(1)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
